import React, { useEffect, useMemo, useState } from 'react';
import Sidebar from './Sidebar';
import { FiCheck, FiX } from 'react-icons/fi';
import { getTimeZones } from '@vvo/tzdb';
import type { TimeZone } from '@vvo/tzdb';

interface TimeInfo {
  time?: string;
  time_zone_id?: string;
}

interface NetworkInfo {
  status?: string;
  network_name?: string;
  signal?: number | string;
}

interface DeviceInfoSidebarProps {
  isOpen: boolean;
  onClose: () => void;
  deviceId?: string;
  deviceName?: string;
  timeInfo?: TimeInfo;
  networkInfo?: NetworkInfo;
  onSaveDeviceName?: (newName: string) => void | Promise<void>;
  onSaveTimeZone?: (newTimeZoneId: string, offsetFactor?: number) => void | Promise<void>;
}

function buildSSID(id?: string) {
  return id ? `SplashMe_${id.replace(/_/g, '')}` : '-';
}

function formatMac(id?: string) {
  return id ? id.replace(/_/g, ':') : '-';
}

function getAuNzTimeZones(): string[] {
  // 优先使用 tzdb 数据集，且只保留 AU/NZ 下属且名称为 Australia/* 或 NZ/Pacific 的条目
  try {
    const zonesSet = new Set<string>();
    const allow = (s: string) =>
      s.startsWith('Australia/') || s === 'NZ' || s === 'NZ-CHAT' || s === 'Pacific/Auckland' || s === 'Pacific/Chatham';

    (getTimeZones() as TimeZone[]).forEach((tz) => {
      const inTarget = tz.countryCode === 'AU' || tz.countryCode === 'NZ';
      if (!inTarget) return;

      const group: string[] = Array.isArray(tz.group) ? tz.group : [];
      group.forEach((g) => {
        if (allow(g)) zonesSet.add(g);
      });

      const name: string | undefined = tz.name;
      if (name && allow(name)) zonesSet.add(name);
    });

    const result = Array.from(zonesSet).sort();
    if (result.length) return result;
  } catch (e) {
    // ignore and fallback
  }

  // 次选：Intl 支持值，依旧只保留 Australia/*、Pacific/Auckland、Pacific/Chatham、NZ
  try {
    const zones: string[] | undefined = (Intl as any).supportedValuesOf?.('timeZone');
    if (zones && Array.isArray(zones)) {
      const filtered = zones.filter(
        (z) => z.startsWith('Australia/') || z === 'Pacific/Auckland' || z === 'Pacific/Chatham' || z === 'NZ'
      );
      return filtered.sort();
    }
  } catch (e) {
    // ignore
  }

  // 静态兜底列表（不含 Antarctica/*）
  return [
    'Australia/Sydney',
    'Australia/Melbourne',
    'Australia/Brisbane',
    'Australia/Adelaide',
    'Australia/Perth',
    'Australia/Darwin',
    'Australia/Hobart',
    'Australia/Broken_Hill',
    'Australia/Lindeman',
    'Australia/Lord_Howe',
    'Australia/Eucla',
    'Pacific/Auckland',
    'Pacific/Chatham',
    'NZ',
  ];
}

function computeOffsetFactor(timeZoneId: string, date: Date = new Date()): number {
  // Offset factor is minutes difference from UTC divided by 15, rounded
  // Parse from shortOffset like "GMT+11" or "GMT+10:30"
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: timeZoneId,
      timeZoneName: 'shortOffset',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h24',
    }).formatToParts(date);
    const tzName = parts.find((p) => p.type === 'timeZoneName')?.value || '';
    const match = tzName.match(/([+-])(\d{1,2})(?::?(\d{2}))?/);
    if (!match) return 0;
    const sign = match[1] === '-' ? -1 : 1;
    const hours = parseInt(match[2], 10);
    const minutes = match[3] ? parseInt(match[3], 10) : 0;
    const totalMinutes = sign * (hours * 60 + minutes);
    return Math.round(totalMinutes / 15);
  } catch (e) {
    return 0;
  }
}

const DeviceInfoSidebar: React.FC<DeviceInfoSidebarProps> = ({
  isOpen,
  onClose,
  deviceId,
  deviceName,
  timeInfo,
  networkInfo,
  onSaveDeviceName,
  onSaveTimeZone,
}) => {
  const [nameEditing, setNameEditing] = useState(false);
  const [nameDraft, setNameDraft] = useState(deviceName || '');
  const [timeZoneDraft, setTimeZoneDraft] = useState<string>(timeInfo?.time_zone_id || '');

  useEffect(() => {
    setNameDraft(deviceName || '');
  }, [deviceName]);

  useEffect(() => {
    setTimeZoneDraft(timeInfo?.time_zone_id || '');
  }, [timeInfo?.time_zone_id]);

  const timeZoneOptions = useMemo(() => getAuNzTimeZones(), []);

  const handleSaveName = async () => {
    if (!onSaveDeviceName) return;
    await onSaveDeviceName(nameDraft);
    setNameEditing(false);
  };

  const handleSaveTimeZone = async (tz: string) => {
    setTimeZoneDraft(tz);
    if (!onSaveTimeZone) return;
    const offsetFactor = computeOffsetFactor(tz, new Date());
    await onSaveTimeZone(tz, offsetFactor);
  };

  return (
    <Sidebar isOpen={isOpen} onClose={onClose} title="Device Information">
      <div className="space-y-6">
        {/* Basics */}
        <div className="p-4 rounded-lg border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
          <h4 className="text-md font-medium text-gray-700 dark:text-gray-200 mb-4">Basics</h4>

          {/* Device Name */}
          <div className="mb-3">
            <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Device Name</div>
            {nameEditing ? (
              <div className="flex items-center gap-2">
                <input
                  className="px-2 py-1 border rounded text-sm bg-white dark:bg-gray-900 border-gray-300 dark:border-gray-700 text-gray-900 dark:text-gray-100 w-full"
                  value={nameDraft}
                  onChange={(e) => setNameDraft(e.target.value)}
                />
                <button
                  type="button"
                  className="inline-flex items-center text-green-600 hover:text-green-700"
                  onClick={handleSaveName}
                  aria-label="Save name"
                >
                  <FiCheck className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  className="inline-flex items-center text-gray-500 hover:text-gray-600"
                  onClick={() => {
                    setNameEditing(false);
                    setNameDraft(deviceName || '');
                  }}
                  aria-label="Cancel edit"
                >
                  <FiX className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <div className="flex items-center justify-between py-1">
                <span className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate max-w-[70%]">
                  {deviceName || '-'}
                </span>
                <button
                  type="button"
                  className="text-primary-600 hover:text-primary-700 text-sm"
                  onClick={() => setNameEditing(true)}
                >
                  Edit
                </button>
              </div>
            )}
          </div>

          {/* Time Zone */}
          <div className="mb-3">
            <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Time Zone</div>
            <select
              className="px-2 py-1 border rounded text-sm bg-white dark:bg-gray-900 border-gray-300 dark:border-gray-700 text-gray-900 dark:text-gray-100 w-full"
              value={timeZoneDraft || ''}
              onChange={(e) => handleSaveTimeZone(e.target.value)}
            >
              <option value="" disabled>
                Select Time Zone
              </option>
              {timeZoneOptions.map((tz) => (
                <option key={tz} value={tz}>
                  {tz}
                </option>
              ))}
            </select>
          </div>

          {/* Current Time */}
          <div className="flex items-center justify-between py-1">
            <span className="text-sm text-gray-500 dark:text-gray-400">Time</span>
            <span className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate max-w-[70%] text-right">
              {timeInfo?.time || '-'}
            </span>
          </div>
        </div>

        {/* Network */}
        <div className="p-4 rounded-lg border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
          <h4 className="text-md font-medium text-gray-700 dark:text-gray-200 mb-4">Network</h4>
          <div className="flex items-center justify-between py-1">
            <span className="text-sm text-gray-500 dark:text-gray-400">Device SSID</span>
            <span className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate max-w-[70%] text-right">
              {buildSSID(deviceId)}
            </span>
          </div>
          <div className="flex items-center justify-between py-1">
            <span className="text-sm text-gray-500 dark:text-gray-400">MAC Address</span>
            <span className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate max-w-[70%] text-right">
              {formatMac(deviceId)}
            </span>
          </div>
          <div className="flex items-center justify-between py-1">
            <span className="text-sm text-gray-500 dark:text-gray-400">Network Status</span>
            <span className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate max-w-[70%] text-right">
              {networkInfo?.status ?? '-'}
            </span>
          </div>
          <div className="flex items-center justify-between py-1">
            <span className="text-sm text-gray-500 dark:text-gray-400">Network Name</span>
            <span className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate max-w-[70%] text-right">
              {networkInfo?.network_name ?? '-'}
            </span>
          </div>
          <div className="flex items-center justify-between py-1">
            <span className="text-sm text-gray-500 dark:text-gray-400">Signal</span>
            <span className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate max-w-[70%] text-right">
              {typeof networkInfo?.signal === 'number' || typeof networkInfo?.signal === 'string' ? networkInfo?.signal : '-'}
            </span>
          </div>
        </div>
      </div>
    </Sidebar>
  );
};

export default DeviceInfoSidebar;